import { useInitialData } from '@notino/react-toolkit/renderer/fragment/useInitialData'
import { useCallback, useRef, useState } from 'react'
import axios from 'axios'

// types
import {
	BasePageData,
	BasePageQuery,
	BasePageRouteProps,
	ExtendedContextProps,
	PageContainer,
	PageContainerQuery,
	PageData,
	PageDataUpdateItem,
	SetPageDataFn,
	SetPageDataFnReturnType,
	UsePageDataReturnType
} from '../types/types'

// utils
import { ERROR_PAGE_CODE } from '../utils/enums'

type LastFailedSetPageDataRequest<TData extends BasePageData> = {
	updates: PageDataUpdateItem<TData> | PageDataUpdateItem<TData>[]
	options?: Parameters<SetPageDataFn<TData>>[1]
}

const resolvePageDataPromise = <T>(data: (() => Promise<T>) | Promise<T>): Promise<T> =>
	// Callers should pass a factory (`() => fetch(...)`) so retry can start a fresh request.
	typeof data === 'function' ? data() : data

/**
 * Custom React hook for managing page data state, updates, and error handling in Notino page containers.
 *
 * This hook:
 * - Initializes page data from the server using useInitialData.
 * - Provides a setPageData function to update page data asynchronously
 * - Handles loading and fetching state.
 * - Implements error handling:
 *   - Handles Axios cancellation and network errors gracefully.
 *   - Maps HTTP error codes (404, 401, 403) to custom error codes for rendering appropriate error pages.
 *
 * @template TData       The shape of the page data.
 * @template TQuery      The shape of the query parameters.
 * @template TRouteProps The shape of additional route props.
 *
 * @param container The page container component (used for SSR/SSG data hydration).
 * @param props     The props passed to the page container.
 * @returns         An object with { pageData, setPageData, isFetching, retryPageRequest }.
 */
const usePageData = <TData extends BasePageData, TQuery extends BasePageQuery, TRouteProps = BasePageRouteProps>(
	container: PageContainer<TData, TQuery, TRouteProps>,
	props: ExtendedContextProps<PageContainerQuery<TQuery> & TRouteProps>
): UsePageDataReturnType<TData> => {
	const initialData = useInitialData(container, props)

	const [pageData, setPageDataInternal] = useState<PageData<TData>>(initialData)
	const [isFetching, setIsFetching] = useState(false)

	// Mirrors pageData without re-rendering; kept on success only (not updated on error).
	const pageDataRef = useRef<PageData<TData>>(pageData)

	/** Stores the last failed setPageData call so retryPageRequest can replay it. Cleared on success. */
	const lastFailedRequestRef = useRef<LastFailedSetPageDataRequest<TData> | null>(null)

	const setPageData: SetPageDataFn<TData> = useCallback(async (updates, options) => {
		// Stored before await so retryPageRequest can replay this call after a failure.
		lastFailedRequestRef.current = { updates, options }

		setIsFetching(true)

		let result: SetPageDataFnReturnType<TData> = { data: null, isCancelTokenError: false }

		try {
			const updateArray = Array.isArray(updates) ? updates : [updates]
			const results = await Promise.all(updateArray.map((u) => resolvePageDataPromise<TData[keyof TData]>(u.data)))

			setPageDataInternal((prevState) => {
				// setPageData only fetches the keys passed in `updates` (e.g. salonsData + filterCountsData).
				// The rest of the page (categoryData, cmsData, …) is not in the response.
				//
				// On retry after a client error, prevState is { success: false } and has none of those fields.
				// pageDataRef still holds the last full successful page, so we use it as the merge base:
				// newState = { ...lastGoodPage, ...partialApiResults }
				const baseState = prevState?.success === true ? prevState : pageDataRef.current

				// API may have succeeded, but there is no valid success snapshot to merge into (error, redirect, or null).
				// Keep current state and skip onSuccess — callers should check result.data?.success before side effects.
				if (!baseState || baseState.success !== true) {
					result = { ...result, data: prevState }
					return prevState
				}

				const newData: Partial<TData> = {}
				updateArray.forEach((update, idx) => {
					newData[update.dataKey] = results[idx]
				})

				const newState = { ...baseState, ...newData }
				const nextState = options?.onSuccess ? options.onSuccess(baseState, newState) : newState

				// Return the data that was stored (including onSuccess merges), not the raw fetch.
				result = { ...result, data: nextState }
				pageDataRef.current = nextState
				return nextState
			})
			lastFailedRequestRef.current = null
		} catch (error) {
			// pageDataRef is intentionally not updated here — it keeps the last good data for retry recovery.
			let data: PageData<TData> = {
				success: false,
				statusCodeToRender: ERROR_PAGE_CODE.GENERAL_ERROR
			}

			if (axios.isCancel(error)) {
				result = {
					...result,
					data: pageDataRef.current,
					isCancelTokenError: true
				}
			} else {
				if (axios.isAxiosError(error)) {
					/**
					 * If the error is a network error, we want to use the previous page data to avoid crashing the app.
					 */
					if (error.code === 'ERR_NETWORK') {
						data = pageDataRef.current
					} else {
						switch (error.response?.status) {
							/**
							 * The Notino layout service currently handles 401 and 403 errors as 404 pages.
							 * However, this logic is prepared to accommodate potential future changes.
							 */
							case 404:
								data = {
									...data,
									statusCodeToRender: ERROR_PAGE_CODE.NOT_FOUND
								}
								break
							case 401:
								data = {
									...data,
									statusCodeToRender: ERROR_PAGE_CODE.UNAUTHORIZED
								}
								break
							case 403:
								data = {
									...data,
									statusCodeToRender: ERROR_PAGE_CODE.FORBIDDEN
								}
								break
							default:
								break
						}
					}
				}

				result = {
					...result,
					data
				}
				setPageDataInternal(data)
			}
		} finally {
			setIsFetching(false)
		}

		return result
	}, [])

	const retryPageRequest = useCallback(() => {
		if (isFetching) {
			return
		}

		const failed = lastFailedRequestRef.current

		if (!failed) {
			// No client setPageData failure stored (e.g. SSR initial-load error) — full reload is the only retry option.
			if (typeof window !== 'undefined') {
				window.location.reload()
			}
			return
		}

		setPageData(failed.updates, failed.options)
	}, [isFetching, setPageData])

	return { pageData, setPageData, isFetching, retryPageRequest }
}

export default usePageData
